import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, setToken } from "./lib/api";
import { LoginPage, type UsuarioLogado } from "./routes/LoginPage";
import { UsuariosPage } from "./routes/UsuariosPage";
import { EquipePage } from "./routes/EquipePage";
import { ChavesApiPage } from "./routes/ChavesApiPage";
import { LixeiraPage } from "./routes/LixeiraPage";
import { PortalMoradorPage } from "./routes/PortalMoradorPage";
import { ResumoPage } from "./routes/ResumoPage";
import { ManutencoesPage } from "./routes/ManutencoesPage";
import { NotificacoesPage } from "./routes/NotificacoesPage";
import { TelefonesEmergenciaPage } from "./routes/TelefonesEmergenciaPage";
import { AvisosPage } from "./routes/AvisosPage";
import { ReservasPage } from "./routes/ReservasPage";
import { EncomendasPage } from "./routes/EncomendasPage";
import { EncomendasMoradorPage } from "./routes/EncomendasMoradorPage";
import { VisitasPortariaPage } from "./routes/VisitasPortariaPage";
import { VisitasMoradorPage } from "./routes/VisitasMoradorPage";
import { ChamadosPage } from "./routes/ChamadosPage";
import { PermissoesProvider, type PermsMap } from "./lib/permissoes";
import { AppShell } from "./components/AppShell";
import { SplashScreen } from "./components/SplashScreen";
import { HomePage } from "./routes/HomePage";
import { CondominioPage } from "./routes/CondominioPage";
import { HistoricoFornecedoresPage } from "./routes/HistoricoFornecedoresPage";
import { AuditoriaFiscalPage } from "./routes/AuditoriaFiscalPage";
import { DashboardPage } from "./routes/DashboardPage";
import { NovoCondominioPage } from "./routes/NovoCondominioPage";
import { ResultadosPage } from "./routes/ResultadosPage";
import { UsoPage } from "./routes/UsoPage";
import { FornecedoresPage } from "./routes/FornecedoresPage";
import { AssistentePage } from "./routes/AssistentePage";
import { LgpdPage } from "./routes/LgpdPage";
import { BackupPage } from "./routes/BackupPage";
import { BackupCondominioPage } from "./routes/BackupCondominioPage";
import { BibliotecaPage } from "./routes/BibliotecaPage";
import { ProfissionaisPage } from "./routes/ProfissionaisPage";
import { GasPage } from "./routes/GasPage";
import { ApresentacoesPage } from "./routes/ApresentacoesPage";
import { AuditoriaPage } from "./routes/AuditoriaPage";
import { RecibosPage } from "./routes/RecibosPage";
import { BoletosPage } from "./routes/BoletosPage";
import { BoletosMoradorPage } from "./routes/BoletosMoradorPage";
import { VotacoesPage } from "./routes/VotacoesPage";
import { VotacoesMoradorPage } from "./routes/VotacoesMoradorPage";

/**
 * Rota inicial — roteamento simples baseado em estado (sem TanStack Router
 * inicialmente, para reduzir setup). Futuro: trocar por TanStack Router.
 */
export type Route =
  | { name: "home" }
  | { name: "novo-condominio" }
  | { name: "condominio"; id: number }
  | { name: "dashboard"; competenciaId: number; condominioId: number; secao?: string }
  | { name: "resultados"; competenciaId: number; condominioId: number; secao?: string }
  | { name: "historico"; condominioId: number; competenciaId?: number }
  | { name: "auditoria-fiscal"; condominioId: number; competenciaId?: number }
  | { name: "fornecedores"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "biblioteca"; condominioId: number }
  | { name: "profissionais"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "recibos"; condominioId: number }
  | { name: "boletos"; condominioId: number }
  | { name: "boletos-morador"; condominioId: number }
  | { name: "votacoes"; condominioId: number }
  | { name: "votacoes-morador"; condominioId: number }
  | { name: "uso" }
  | { name: "lgpd" }
  | { name: "backup" }
  | { name: "backup-condominio" }
  | { name: "usuarios" }
  | { name: "chaves-api" }
  | { name: "lixeira" }
  | { name: "auditoria" }
  | { name: "portal"; condominioId: number }
  | { name: "resumo"; condominioId: number }
  | { name: "manutencoes"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "notificacoes"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "telefones"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "avisos"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "reservas"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "encomendas"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "encomendas-morador"; condominioId: number }
  | { name: "visitas"; condominioId: number; de?: "portal" | "condominio" }
  | { name: "visitas-morador"; condominioId: number }
  | { name: "gas"; condominioId: number; mesReferencia?: string; voltarResultados?: { competenciaId: number } }
  | { name: "chamados"; condominioId: number }
  | { name: "apresentacoes"; condominioId: number }
  | { name: "equipe"; condominioId: number }
  | { name: "assistente"; condominioId?: number };

export function App() {
  const qc = useQueryClient();
  const [route, setRoute] = useState<Route>({ name: "home" });
  const [backendPronto, setBackendPronto] = useState(false);

  // Login por usuário (multiusuário). usuario === null = não logado.
  const [usuario, setUsuario] = useState<UsuarioLogado | null>(null);
  const [authChecado, setAuthChecado] = useState(false);
  // Permissões do usuário logado (para esconder botões de escrita).
  const [permissoes, setPermissoes] = useState<PermsMap>({});
  const [ehGestor, setEhGestor] = useState(false);      // Dono ou síndico de algum condomínio
  const [sindicoEm, setSindicoEm] = useState<number[]>([]);
  // id+nome dos condomínios do usuário — o Portal usa para deixar quem tem mais
  // de um condomínio ESCOLHER onde quer trabalhar (seletor no cabeçalho).
  const [condominiosInfo, setCondominiosInfo] = useState<{ id: number; nome: string }[]>([]);

  // Ao abrir: se há um token salvo, valida com /me; senão, cai no login.
  useEffect(() => {
    if (!backendPronto) return;
    let vivo = true;
    api
      .get<{ usuario: UsuarioLogado; permissoes: PermsMap; eh_gestor?: boolean; sindico_em?: number[]; condominios?: number[]; condominios_info?: { id: number; nome: string }[] }>("/usuarios/me")
      .then((r) => {
        if (!vivo) return;
        setUsuario(r.usuario);
        setPermissoes(r.permissoes ?? {});
        setEhGestor(!!r.eh_gestor);
        setSindicoEm(r.sindico_em ?? []);
        setCondominiosInfo(r.condominios_info ?? []);
        // Condômino (não é Dono nem gestor) cai direto no Portal do Condômino
        // (acesso mobile). Gestores/Dono seguem para o painel de auditoria.
        const conds = r.condominios ?? [];
        if (!r.usuario.is_dono && !r.eh_gestor && conds.length > 0) {
          setRoute({ name: "portal", condominioId: conds[0] });
        }
      })
      .catch(() => vivo && setUsuario(null))
      .finally(() => vivo && setAuthChecado(true));
    return () => {
      vivo = false;
    };
  }, [backendPronto]);

  // Depois de logar (ou trocar de usuário), recarrega as permissões.
  useEffect(() => {
    if (!usuario) {
      setPermissoes({});
      setEhGestor(false);
      setSindicoEm([]);
      setCondominiosInfo([]);
      return;
    }
    let vivo = true;
    api
      .get<{ permissoes: PermsMap; eh_gestor?: boolean; sindico_em?: number[]; condominios?: number[]; condominios_info?: { id: number; nome: string }[] }>("/usuarios/me")
      .then((r) => {
        if (!vivo) return;
        setPermissoes(r.permissoes ?? {});
        setEhGestor(!!r.eh_gestor);
        setSindicoEm(r.sindico_em ?? []);
        setCondominiosInfo(r.condominios_info ?? []);
        // Condômino (não é Dono nem gestor) vai direto ao Portal — inclusive DEPOIS do
        // login. IMPORTANTE: redireciona SEM depender da tela atual. A versão antiga só
        // trocava quando estava na "home"; no fluxo Sair→entrar a resposta às vezes
        // chegava com a tela já em "condominio" (403 → "Carregando… unidades" travado),
        // e o morador ficava preso. Como ele não é gestor, não há navegação de gestão
        // para atrapalhar: sempre mandamos ao Portal.
        const conds = r.condominios ?? [];
        if (!usuario.is_dono && !r.eh_gestor && conds.length > 0) {
          setRoute((atual) => (atual.name === "portal" ? atual : { name: "portal", condominioId: conds[0] }));
        }
      })
      .catch(() => {
        /* ignore — botões apenas não aparecem */
      });
    return () => {
      vivo = false;
    };
  }, [usuario?.id]);

  // Rede de segurança: um condômino puro (não Dono, não gestor) NUNCA pode ficar
  // preso numa tela de gestão. Se por qualquer caminho ele acabar na tela do
  // condomínio (que ele não tem permissão de abrir → "Carregando… unidades"),
  // mandamos ao Portal. Cobre corridas de estado que o redirecionamento do login
  // eventualmente não pegue.
  useEffect(() => {
    if (!usuario || usuario.is_dono || ehGestor) return;
    if (condominiosInfo.length === 0) return;
    if (route.name === "condominio" || route.name === "home") {
      setRoute({ name: "portal", condominioId: condominiosInfo[0].id });
    }
  }, [route, usuario, ehGestor, condominiosInfo]);

  // Se a sessão expirar durante o uso, volta para o login (e limpa o estado
  // do usuário anterior: cache de dados + tela atual).
  useEffect(() => {
    const onSemSessao = () => {
      setUsuario(null);
      setPermissoes({});
      setEhGestor(false);
      setSindicoEm([]);
      setCondominiosInfo([]);
      setRoute({ name: "home" });
      qc.clear();
    };
    window.addEventListener("sindicoapp:sem-sessao", onSemSessao);
    return () => window.removeEventListener("sindicoapp:sem-sessao", onSemSessao);
  }, [qc]);

  async function sair() {
    try {
      await api.post("/usuarios/logout");
    } catch {
      /* ignore */
    }
    setToken(null);
    setUsuario(null);
    setPermissoes({});
    setEhGestor(false);
    setSindicoEm([]);
    setCondominiosInfo([]);
    // IMPORTANTE: zera a tela e o cache para o próximo usuário não herdar
    // nada do anterior (lista de condomínios, usuários, permissões…).
    setRoute({ name: "home" });
    qc.clear();
  }

  // Ao trocar de tela, volta ao topo — assim o cabeçalho (Backup,
  // Histórico…) sempre aparece e não fica escondido pela rolagem anterior.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [route]);

  if (!backendPronto) {
    return <SplashScreen onPronto={() => setBackendPronto(true)} />;
  }

  // Login por usuário é a PRIMEIRA barreira. Enquanto valida a sessão, aguarda.
  if (!authChecado) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 text-sm text-slate-400">
        Carregando…
      </div>
    );
  }
  if (!usuario) {
    return (
      <LoginPage
        onEntrar={(u, token) => {
          // Novo usuário: zera qualquer cache/tela que tenha sobrado do
          // usuário anterior antes de entrar.
          qc.clear();
          setRoute({ name: "home" });
          setToken(token);
          setUsuario(u);
        }}
      />
    );
  }

  return (
    <PermissoesProvider
      isDono={usuario.is_dono}
      permissoes={permissoes}
      ehGestor={ehGestor}
      sindicoEm={sindicoEm}
    >
    <AppShell
      usuario={{ nome: usuario.nome, is_dono: usuario.is_dono }}
      ehGestor={ehGestor}
      onAbrirAssinaturas={(usuario.is_dono || ehGestor) ? () => setRoute({ name: "uso" }) : undefined}
      onSair={sair}
      onAbrirUsuarios={usuario.is_dono ? () => setRoute({ name: "usuarios" }) : undefined}
      onAbrirChaves={usuario.is_dono ? () => setRoute({ name: "chaves-api" }) : undefined}
      onAbrirLixeira={usuario.is_dono ? () => setRoute({ name: "lixeira" }) : undefined}
      onAbrirAuditoria={usuario.is_dono ? () => setRoute({ name: "auditoria" }) : undefined}
      onNavigateHome={() => setRoute({ name: "home" })}
      onAbrirLgpd={ehGestor ? () => setRoute({ name: "lgpd" }) : undefined}
      onAbrirBackup={usuario.is_dono ? () => setRoute({ name: "backup" }) : undefined}
      onAbrirBackupCondominio={(ehGestor && !usuario.is_dono) ? () => setRoute({ name: "backup-condominio" }) : undefined}
    >
      {route.name === "home" && (
        <HomePage
          onAbrir={(id) => setRoute({ name: "condominio", id })}
          onNovo={() => setRoute({ name: "novo-condominio" })}
          onAbrirUso={ehGestor ? () => setRoute({ name: "uso" }) : undefined}
          onAbrirAssistente={() => setRoute({ name: "assistente" })}
        />
      )}
      {route.name === "novo-condominio" && (
        <NovoCondominioPage
          onCancelar={() => setRoute({ name: "home" })}
          onCriado={(id) => setRoute({ name: "condominio", id })}
        />
      )}
      {route.name === "condominio" && (
        <CondominioPage
          condominioId={route.id}
          onVoltar={() => setRoute({ name: "home" })}
          onVerResultados={(competenciaId) =>
            setRoute({ name: "dashboard", competenciaId, condominioId: route.id })
          }
          onAbrirBiblioteca={() =>
            setRoute({ name: "biblioteca", condominioId: route.id })
          }
          onAbrirFornecedores={() =>
            setRoute({ name: "fornecedores", condominioId: route.id, de: "condominio" })
          }
          onAbrirProfissionais={() =>
            setRoute({ name: "profissionais", condominioId: route.id, de: "condominio" })
          }
          onAbrirRecibos={() =>
            setRoute({ name: "recibos", condominioId: route.id })
          }
          onAbrirBoletos={() =>
            setRoute({ name: "boletos", condominioId: route.id })
          }
          onAbrirVotacoes={() =>
            setRoute({ name: "votacoes", condominioId: route.id })
          }
          onAbrirAvisos={() =>
            setRoute({ name: "avisos", condominioId: route.id, de: "condominio" })
          }
          onAbrirManutencoes={() =>
            setRoute({ name: "manutencoes", condominioId: route.id, de: "condominio" })
          }
          onAbrirNotificacoes={() =>
            setRoute({ name: "notificacoes", condominioId: route.id, de: "condominio" })
          }
          onAbrirReservas={() =>
            setRoute({ name: "reservas", condominioId: route.id, de: "condominio" })
          }
          onAbrirEncomendas={() =>
            setRoute({ name: "encomendas", condominioId: route.id })
          }
          onAbrirVisitas={() =>
            setRoute({ name: "visitas", condominioId: route.id, de: "condominio" })
          }
          onAbrirTelefones={() =>
            setRoute({ name: "telefones", condominioId: route.id, de: "condominio" })
          }
          onAbrirGas={() => setRoute({ name: "gas", condominioId: route.id })}
          onAbrirApresentacoes={() => setRoute({ name: "apresentacoes", condominioId: route.id })}
          onAbrirAssistente={() =>
            setRoute({ name: "assistente", condominioId: route.id })
          }
          onAbrirEquipe={() => setRoute({ name: "equipe", condominioId: route.id })}
          onAbrirPortal={() => setRoute({ name: "portal", condominioId: route.id })}
        />
      )}
      {route.name === "equipe" && (
        <EquipePage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "condominio", id: route.condominioId })}
        />
      )}
      {route.name === "auditoria-fiscal" && (
        <AuditoriaFiscalPage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute(
              route.competenciaId
                ? { name: "dashboard", competenciaId: route.competenciaId, condominioId: route.condominioId, secao: "relatorios" }
                : { name: "condominio", id: route.condominioId },
            )
          }
        />
      )}
      {route.name === "dashboard" && (
        <DashboardPage
          competenciaId={route.competenciaId}
          condominioId={route.condominioId}
          secao={route.secao}
          onVoltar={() =>
            setRoute({ name: "condominio", id: route.condominioId })
          }
          onAbrirRelatorio={(relatorio) =>
            setRoute({
              name: "resultados",
              competenciaId: route.competenciaId,
              condominioId: route.condominioId,
              secao: relatorio,
            })
          }
          onAbrirHistorico={() =>
            setRoute({ name: "historico", condominioId: route.condominioId, competenciaId: route.competenciaId })
          }
          onAbrirAuditoriaFiscal={() =>
            setRoute({ name: "auditoria-fiscal", condominioId: route.condominioId, competenciaId: route.competenciaId })
          }
        />
      )}
      {route.name === "resultados" && (
        <ResultadosPage
          competenciaId={route.competenciaId}
          secao={route.secao}
          onVoltar={() =>
            setRoute({
              name: "dashboard",
              competenciaId: route.competenciaId,
              condominioId: route.condominioId,
              secao: "relatorios",
            })
          }
          onAbrirGasRateio={(mesReferencia) =>
            setRoute({
              name: "gas",
              condominioId: route.condominioId,
              mesReferencia,
              voltarResultados: { competenciaId: route.competenciaId },
            })
          }
        />
      )}
      {route.name === "historico" && (
        <HistoricoFornecedoresPage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute(
              route.competenciaId
                ? { name: "dashboard", competenciaId: route.competenciaId, condominioId: route.condominioId, secao: "relatorios" }
                : { name: "condominio", id: route.condominioId },
            )
          }
        />
      )}
      {route.name === "uso" && (
        <UsoPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "lgpd" && (
        <LgpdPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "backup" && (
        <BackupPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "backup-condominio" && (
        <BackupCondominioPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "usuarios" && (
        <UsuariosPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "chaves-api" && (
        <ChavesApiPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "lixeira" && (
        <LixeiraPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "auditoria" && (
        <AuditoriaPage onVoltar={() => setRoute({ name: "home" })} />
      )}
      {route.name === "assistente" && (
        <AssistentePage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute(
              route.condominioId
                ? { name: "condominio", id: route.condominioId }
                : { name: "home" },
            )
          }
        />
      )}
      {route.name === "fornecedores" && (
        <FornecedoresPage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute({ name: "condominio", id: route.condominioId })
          }
        />
      )}
      {route.name === "biblioteca" && (
        <BibliotecaPage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute({ name: "condominio", id: route.condominioId })
          }
        />
      )}
      {route.name === "profissionais" && (
        <ProfissionaisPage
          condominioId={route.condominioId}
          somenteLeitura={route.de !== "condominio"}
          onVoltar={() =>
            setRoute(
              route.de === "condominio"
                ? { name: "condominio", id: route.condominioId }
                : { name: "portal", condominioId: route.condominioId },
            )
          }
        />
      )}
      {route.name === "apresentacoes" && (
        <ApresentacoesPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "condominio", id: route.condominioId })}
        />
      )}
      {route.name === "gas" && (
        <GasPage
          condominioId={route.condominioId}
          mesReferencia={route.mesReferencia}
          voltarLabel={route.voltarResultados ? "← Voltar ao Resumo de DOC's" : undefined}
          onVoltar={() =>
            route.voltarResultados
              ? setRoute({
                  name: "resultados",
                  competenciaId: route.voltarResultados.competenciaId,
                  condominioId: route.condominioId,
                  secao: "docs",
                })
              : setRoute({ name: "condominio", id: route.condominioId })
          }
        />
      )}
      {route.name === "recibos" && (
        <RecibosPage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute({ name: "condominio", id: route.condominioId })
          }
        />
      )}
      {route.name === "boletos" && (
        <BoletosPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "condominio", id: route.condominioId })}
        />
      )}
      {route.name === "boletos-morador" && (
        <BoletosMoradorPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "portal", condominioId: route.condominioId })}
        />
      )}
      {route.name === "votacoes" && (
        <VotacoesPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "condominio", id: route.condominioId })}
        />
      )}
      {route.name === "votacoes-morador" && (
        <VotacoesMoradorPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "portal", condominioId: route.condominioId })}
        />
      )}
      {route.name === "portal" && (
        <PortalMoradorPage
          condominioId={route.condominioId}
          condominios={condominiosInfo}
          onTrocarCondominio={(id) => setRoute({ name: "portal", condominioId: id })}
          usuarioNome={usuario.nome}
          onAbrirAvisos={() => setRoute({ name: "avisos", condominioId: route.condominioId })}
          onAbrirProfissionais={() => setRoute({ name: "profissionais", condominioId: route.condominioId })}
          onAbrirReservas={() => setRoute({ name: "reservas", condominioId: route.condominioId })}
          onAbrirChamados={() => setRoute({ name: "chamados", condominioId: route.condominioId })}
          onAbrirResumo={() => setRoute({ name: "resumo", condominioId: route.condominioId })}
          onAbrirManutencoes={() => setRoute({ name: "manutencoes", condominioId: route.condominioId })}
          onAbrirNotificacoes={() => setRoute({ name: "notificacoes", condominioId: route.condominioId })}
          onAbrirTelefones={() => setRoute({ name: "telefones", condominioId: route.condominioId })}
          onAbrirBoletos={() => setRoute({ name: "boletos-morador", condominioId: route.condominioId })}
          onAbrirVotacoes={() => setRoute({ name: "votacoes-morador", condominioId: route.condominioId })}
          onAbrirEncomendas={() => setRoute({ name: "encomendas-morador", condominioId: route.condominioId })}
          onAbrirEncomendasGestao={() => setRoute({ name: "encomendas", condominioId: route.condominioId, de: "portal" })}
          onAbrirVisitas={() => setRoute({ name: "visitas-morador", condominioId: route.condominioId })}
          onAbrirVisitasGestao={() => setRoute({ name: "visitas", condominioId: route.condominioId, de: "portal" })}
          onSair={sair}
          // Gestor/Dono está só visualizando: volta ao painel do condomínio.
          onVoltar={ehGestor ? () => setRoute({ name: "condominio", id: route.condominioId }) : undefined}
        />
      )}
      {route.name === "resumo" && (
        <ResumoPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "portal", condominioId: route.condominioId })}
        />
      )}
      {route.name === "manutencoes" && (
        <ManutencoesPage
          condominioId={route.condominioId}
          gestao={route.de === "condominio"}
          onVoltar={() =>
            setRoute(
              route.de === "condominio"
                ? { name: "condominio", id: route.condominioId }
                : { name: "portal", condominioId: route.condominioId },
            )
          }
        />
      )}
      {route.name === "notificacoes" && (
        <NotificacoesPage
          condominioId={route.condominioId}
          gestao={route.de === "condominio"}
          onVoltar={() =>
            setRoute(
              route.de === "condominio"
                ? { name: "condominio", id: route.condominioId }
                : { name: "portal", condominioId: route.condominioId },
            )
          }
        />
      )}
      {route.name === "telefones" && (
        <TelefonesEmergenciaPage
          condominioId={route.condominioId}
          somenteLeitura={route.de !== "condominio"}
          onVoltar={() =>
            setRoute(
              route.de === "condominio"
                ? { name: "condominio", id: route.condominioId }
                : { name: "portal", condominioId: route.condominioId },
            )
          }
        />
      )}
      {route.name === "avisos" && (
        <AvisosPage
          condominioId={route.condominioId}
          gestao={route.de === "condominio"}
          onVoltar={() =>
            setRoute(
              route.de === "condominio"
                ? { name: "condominio", id: route.condominioId }
                : { name: "portal", condominioId: route.condominioId },
            )
          }
        />
      )}
      {route.name === "reservas" && (
        <ReservasPage
          condominioId={route.condominioId}
          gestao={route.de === "condominio"}
          onVoltar={() =>
            setRoute(
              route.de === "condominio"
                ? { name: "condominio", id: route.condominioId }
                : { name: "portal", condominioId: route.condominioId },
            )
          }
        />
      )}
      {route.name === "encomendas" && (
        <EncomendasPage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute(
              route.de === "portal"
                ? { name: "portal", condominioId: route.condominioId }
                : { name: "condominio", id: route.condominioId },
            )
          }
        />
      )}
      {route.name === "encomendas-morador" && (
        <EncomendasMoradorPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "portal", condominioId: route.condominioId })}
        />
      )}
      {route.name === "visitas" && (
        <VisitasPortariaPage
          condominioId={route.condominioId}
          onVoltar={() =>
            setRoute(
              route.de === "portal"
                ? { name: "portal", condominioId: route.condominioId }
                : { name: "condominio", id: route.condominioId },
            )
          }
        />
      )}
      {route.name === "visitas-morador" && (
        <VisitasMoradorPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "portal", condominioId: route.condominioId })}
        />
      )}
      {route.name === "chamados" && (
        <ChamadosPage
          condominioId={route.condominioId}
          onVoltar={() => setRoute({ name: "portal", condominioId: route.condominioId })}
        />
      )}
    </AppShell>
    </PermissoesProvider>
  );
}
